/**
 * Plate's markdown serializer writes some characters as numeric references
 * (`&#x20;` for a trailing space, `&#x54;` for a letter next to emphasis).
 * Notes display them as the real character, but copy and export were keeping
 * the reference text.
 */

const CHARACTER_REFERENCE_RE =
  /&(#(?:[xX][0-9a-fA-F]+|\d+)|[A-Za-z][A-Za-z0-9]+);/g;

const NAMED_REFERENCES: Record<string, string> = {
  amp: "&",
  apos: "'",
  gt: ">",
  lt: "<",
  nbsp: "\u00A0",
  quot: '"',
};

function decodeReferenceBody(body: string): string | undefined {
  if (body.startsWith("#")) {
    const hex = body[1] === "x" || body[1] === "X";
    const digits = body.slice(hex ? 2 : 1);
    if (!digits || (hex && !/^[0-9a-fA-F]+$/.test(digits))) return undefined;
    const code = Number.parseInt(digits, hex ? 16 : 10);
    if (!isDecodableCodePoint(code)) return undefined;
    return String.fromCodePoint(code);
  }

  return NAMED_REFERENCES[body.toLowerCase()];
}

function isDecodableCodePoint(code: number): boolean {
  if (!Number.isInteger(code)) return false;
  if (code === 0x09 || code === 0x0a || code === 0x0d) return true;
  if (code >= 0x20 && code <= 0xd7ff) return true;
  return code >= 0xe000 && code <= 0x10ffff;
}

/** `<`, `>` and `&` stay encoded so export does not turn them into tags. */
function isMarkupCharacter(char: string): boolean {
  return char === "<" || char === ">" || char === "&";
}

function isStructuralWhitespace(char: string): boolean {
  return char === " " || char === "\t";
}

/**
 * A decoded space or tab would turn this line into a heading, a list, a
 * blockquote, or an indented code block. Marked treats a tab after `#` or a
 * list marker as the space those blocks need, and 1–3 spaces before `>`
 * start a quote. `>quote` is already a quote, so a space after `>` is fine.
 */
const BLOCK_LINE_RE =
  /^(?: {4,}|\t| {0,3}>| {0,3}#{1,6}(?:[ \t]|$)| {0,3}[-*+][ \t]| {0,3}\d{1,9}[.)][ \t])/;
const INLINE_CODE_RE = /(`+)[\s\S]*?\1/g;
const INLINE_CODE_PLACEHOLDER_RE = /\uE000NOVONOTE_ENTITY_CODE_(\d+)\uE001/g;

function expandStructuralWhitespace(line: string): string {
  return line.replace(CHARACTER_REFERENCE_RE, (match, body: string) => {
    const decoded = decodeReferenceBody(body);
    if (decoded !== undefined && isStructuralWhitespace(decoded)) {
      return decoded;
    }
    return match;
  });
}

function lineWouldGainBlockStructure(line: string): boolean {
  const expanded = expandStructuralWhitespace(line);
  return BLOCK_LINE_RE.test(expanded) && !BLOCK_LINE_RE.test(line);
}

/** Letters, numbers, and a non-breaking space are safe to store as themselves. */
function isSafeMarkdownCharacter(char: string): boolean {
  if (char === "\u00A0") return true;
  return /^[\p{L}\p{N}\p{M}]$/u.test(char);
}

function replaceReferences(
  value: string,
  keep: (decoded: string) => boolean
): string {
  if (!value.includes("&")) return value;
  return value.replace(CHARACTER_REFERENCE_RE, (match, body: string) => {
    const decoded = decodeReferenceBody(body);
    if (decoded === undefined || !keep(decoded)) return match;
    return decoded;
  });
}

function decodeProseLines(
  value: string,
  keep: (decoded: string, line: string) => boolean
): string {
  const codeSpans: string[] = [];
  const protectedValue = value.replace(INLINE_CODE_RE, (span) => {
    const index = codeSpans.push(span) - 1;
    return `\uE000NOVONOTE_ENTITY_CODE_${index}\uE001`;
  });

  const decoded = protectedValue
    .split("\n")
    .map((line) => replaceReferences(line, (char) => keep(char, line)))
    .join("\n");

  return decoded.replace(
    INLINE_CODE_PLACEHOLDER_RE,
    (match, index: string) => codeSpans[Number(index)] ?? match
  );
}

/** Leave fenced code untouched. Character references there are sample text. */
function decodeOutsideFencedCode(
  value: string,
  keep: (decoded: string, line: string) => boolean
): string {
  const parts = value.split(/(\r?\n)/);
  let fence: { character: string; length: number } | null = null;
  let pending = "";
  let result = "";

  const flush = () => {
    if (!pending) return;
    result += decodeProseLines(pending, keep);
    pending = "";
  };

  for (let index = 0; index < parts.length; index += 2) {
    const line = parts[index] ?? "";
    const newline = parts[index + 1] ?? "";
    const fenceMatch = line.match(/^[ \t]{0,3}(`{3,}|~{3,})/);

    if (!fence && fenceMatch) {
      flush();
      fence = {
        character: fenceMatch[1][0],
        length: fenceMatch[1].length,
      };
      result += line + newline;
      continue;
    }

    if (fence) {
      result += line + newline;
      if (
        fenceMatch &&
        fenceMatch[1][0] === fence.character &&
        fenceMatch[1].length >= fence.length &&
        /^[ \t]*$/.test(line.slice(fenceMatch[0].length))
      ) {
        fence = null;
      }
      continue;
    }

    pending += line + newline;
  }

  flush();
  return result;
}

function keepExportedCharacter(decoded: string, line: string): boolean {
  if (isMarkupCharacter(decoded)) return false;
  if (isStructuralWhitespace(decoded)) {
    return !lineWouldGainBlockStructure(line);
  }
  return true;
}

function keepSavedCharacter(decoded: string, line: string): boolean {
  if (isStructuralWhitespace(decoded)) {
    return !lineWouldGainBlockStructure(line);
  }
  return isSafeMarkdownCharacter(decoded);
}

/**
 * Decode HTML character references in note text for copy and export.
 * `<`, `>` and `&` stay encoded so a comparison is not parsed as a tag.
 * A space stays encoded when decoding it would turn the line into a heading,
 * list, quote, or code block. Fenced and inline code are left alone.
 */
export function decodeHtmlCharacterReferences(value: string): string {
  if (!value.includes("&")) return value;
  return decodeOutsideFencedCode(value, keepExportedCharacter);
}

/**
 * Decode references that will not change markdown structure. `&lt;`, `&gt;`,
 * and `&amp;` stay encoded so a saved note does not grow a raw tag. A space
 * stays encoded when decoding it would turn the line into a heading, list,
 * quote, or indented code block.
 */
export function decodeSafeMarkdownCharacterReferences(value: string): string {
  if (!value.includes("&")) return value;
  return decodeOutsideFencedCode(value, keepSavedCharacter);
}
